import React, { memo } from 'react';
import { TableCell as MuiTableCell, Typography, Chip } from '@mui/material';
import { TabLink } from '@/shared/ui/navigation/TabLink';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import {
  DataTableColumn,
  isStringColumn,
  isNumberColumn,
  isDateColumn,
  isDateTimeColumn,
  isLabelColumn,
} from '../types';
import { useDataTableTheme } from '../theme';

dayjs.locale('ko');

interface IProps {
  column: DataTableColumn;
  value: any;
  row?: Record<string, any>;
}

/**
 * 숫자 포맷팅 함수
 */
function formatNumber(value: number, column: import('../types').NumberColumn): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '-';
  }

  const { format = 'decimal', decimalPlaces = 0, useThousandSeparator = true } = column;

  // 천 단위 구분자 사용 여부에 따라 포맷팅 옵션 설정
  const localeOptions: Intl.NumberFormatOptions = {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
    useGrouping: useThousandSeparator, // 천 단위 구분자 사용 여부
  };

  switch (format) {
    case 'integer':
      return useThousandSeparator
        ? Math.round(value).toLocaleString('ko-KR')
        : Math.round(value).toString();
    case 'decimal':
      return useThousandSeparator
        ? value.toLocaleString('ko-KR', localeOptions)
        : value.toFixed(decimalPlaces);
    case 'currency':
      const currencyValue = useThousandSeparator
        ? value.toLocaleString('ko-KR', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })
        : Math.round(value).toString();
      return `${currencyValue}원`;
    case 'percentage':
      const percentageValue = useThousandSeparator
        ? value.toLocaleString('ko-KR', localeOptions)
        : value.toFixed(decimalPlaces);
      return `${percentageValue}%`;
    default:
      return useThousandSeparator ? value.toLocaleString('ko-KR') : value.toString();
  }
}

/**
 * 날짜 포맷팅 함수
 */
function formatDate(value: any, format?: string): string {
  if (!value) return '-';

  const date = dayjs(value);
  if (!date.isValid()) return String(value);

  return format ? date.format(format) : date.format('YYYY. MM. DD.');
}

/**
 * 날짜시간 포맷팅 함수
 */
function formatDateTime(value: any, format?: string): string {
  if (!value) return '-';

  const date = dayjs(value);
  if (!date.isValid()) return String(value);

  return format ? date.format(format) : date.format('YYYY. MM. DD. A hh:mm');
}

/**
 * 테이블 셀 컴포넌트
 * value와 row가 변경되지 않으면 리렌더링하지 않음
 */
function TableCellComponent({ column, value, row }: IProps) {
  const { getCellSx, tokens } = useDataTableTheme();
  const align = column.align || 'left';
  const cellSx = getCellSx();

  // 조건부 포맷팅 적용 (텍스트에만 적용)
  const conditionalSx = column.conditionalFormatting?.condition(value, row)
    ? {
        ...column.conditionalFormatting.sx,
        // inline-block과 padding이 함께 적용될 때 높이를 일정하게 유지
        lineHeight: '1.43', // Typography body2의 기본 lineHeight
        verticalAlign: 'middle',
      }
    : {};

  // 링크가 있는 경우 렌더링할 내용
  const renderContent = (content: React.ReactNode) => {
    if (column.link && row) {
      const to = column.link.to(row);
      return <TabLink to={to}>{content}</TabLink>;
    }
    return content;
  };

  // 문자열 타입
  if (isStringColumn(column)) {
    const displayValue = value ?? '-';
    return (
      <MuiTableCell
        align={align}
        sx={{
          ...cellSx,
          ...(column.width && {
            width: column.width,
            minWidth: column.width,
            maxWidth: column.width,
          }),
        }}
      >
        <Typography
          variant="body2"
          noWrap
          sx={{ color: tokens.cell.text.default, ...conditionalSx }}
        >
          {renderContent(displayValue)}
        </Typography>
      </MuiTableCell>
    );
  }

  // 숫자 타입
  if (isNumberColumn(column)) {
    const displayValue = formatNumber(value, column);
    return (
      <MuiTableCell
        align={align || 'right'}
        sx={{
          ...cellSx,
          ...(column.width && {
            width: column.width,
            minWidth: column.width,
            maxWidth: column.width,
          }),
        }}
      >
        <Typography
          variant="body2"
          noWrap
          sx={{ color: tokens.cell.text.default, ...conditionalSx }}
        >
          {renderContent(displayValue)}
        </Typography>
      </MuiTableCell>
    );
  }

  // 날짜 타입
  if (isDateColumn(column)) {
    const displayValue = formatDate(value, column.format);
    return (
      <MuiTableCell
        align={align}
        sx={{
          ...cellSx,
          ...(column.width && {
            width: column.width,
            minWidth: column.width,
            maxWidth: column.width,
          }),
        }}
      >
        <Typography
          variant="body2"
          noWrap
          sx={{ color: tokens.cell.text.default, ...conditionalSx }}
        >
          {renderContent(displayValue)}
        </Typography>
      </MuiTableCell>
    );
  }

  // 날짜시간 타입
  if (isDateTimeColumn(column)) {
    const displayValue = formatDateTime(value, column.format);
    return (
      <MuiTableCell
        align={align}
        sx={{
          ...cellSx,
          ...(column.width && {
            width: column.width,
            minWidth: column.width,
            maxWidth: column.width,
          }),
        }}
      >
        <Typography
          variant="body2"
          noWrap
          sx={{ color: tokens.cell.text.default, ...conditionalSx }}
        >
          {renderContent(displayValue)}
        </Typography>
      </MuiTableCell>
    );
  }

  // 라벨 타입
  if (isLabelColumn(column)) {
    const {
      color: staticColor = 'default',
      getColor,
      variant: staticVariant = 'filled',
      getVariant,
    } = column;
    const color = getColor ? getColor(value, row) : staticColor;
    const variant = getVariant ? getVariant(value, row) : staticVariant;
    const labelValue =
      value === null || value === undefined
        ? '-'
        : typeof value === 'object'
          ? JSON.stringify(value)
          : String(value);

    return (
      <MuiTableCell
        align={align}
        sx={{
          ...cellSx,
          ...(column.width && {
            width: column.width,
            minWidth: column.width,
            maxWidth: column.width,
          }),
        }}
      >
        <Chip
          label={labelValue}
          color={color === 'default' ? undefined : color}
          variant={variant}
          size="small"
          sx={{
            height: '24px',
            fontSize: '0.75rem',
            fontWeight: 500,
          }}
        />
      </MuiTableCell>
    );
  }

  // 기본 타입 (string, number 등) - 타입 가드를 통과하지 못한 경우
  const displayValue =
    value === null || value === undefined
      ? '-'
      : typeof value === 'object'
        ? JSON.stringify(value)
        : String(value);

  // column.width는 BaseColumn에 있으므로 모든 타입에 존재
  const columnWidth = (column as { width?: string }).width;

  return (
    <MuiTableCell
      align={align}
      sx={{
        ...cellSx,
        ...(columnWidth && { width: columnWidth, minWidth: columnWidth, maxWidth: columnWidth }),
      }}
    >
      <Typography variant="body2" noWrap sx={{ color: tokens.cell.text.default, ...conditionalSx }}>
        {renderContent(displayValue)}
      </Typography>
    </MuiTableCell>
  );
}

/**
 * TableCell을 memo로 감싸서 실제 표시되는 내용(텍스트, 컬러, 링크)이 변경되지 않으면 리렌더링하지 않도록 최적화
 * row 객체의 참조가 변경되어도 실제 값이 동일하면 리렌더링하지 않음
 */
export const TableCell = memo(TableCellComponent, (prevProps, nextProps) => {
  // column은 일반적으로 변경되지 않으므로 비교에서 제외
  const column = prevProps.column;
  const columnId = column.id;

  // 1. value(텍스트)가 변경되었는지 확인
  if (prevProps.value !== nextProps.value) {
    return false; // 값이 변경되었으므로 리렌더링 필요
  }

  // 2. row 객체가 변경되었는지 확인
  if (prevProps.row !== nextProps.row) {
    // row 객체의 참조가 변경되었지만, 실제로 필요한 값이 변경되었는지 확인
    const prevRowValue = prevProps.row?.[columnId];
    const nextRowValue = nextProps.row?.[columnId];

    // row의 해당 column 값이 변경되었는지 확인
    if (prevRowValue !== nextRowValue) {
      return false; // row의 해당 column 값이 변경되었으므로 리렌더링 필요
    }

    // 3. 동적 속성들(컬러, 링크, 조건부 포맷팅)이 실제로 변경되었는지 확인
    // conditionalFormatting 조건 확인
    if (column.conditionalFormatting) {
      const prevCondition = column.conditionalFormatting.condition(prevProps.value, prevProps.row);
      const nextCondition = column.conditionalFormatting.condition(nextProps.value, nextProps.row);
      if (prevCondition !== nextCondition) {
        return false; // 조건부 포맷팅 조건이 변경되었으므로 리렌더링 필요
      }
    }

    // getColor 확인 (라벨의 색상) - LabelColumn에만 있음
    if (isLabelColumn(column) && column.getColor) {
      const prevColor = column.getColor(prevProps.value, prevProps.row);
      const nextColor = column.getColor(nextProps.value, nextProps.row);
      if (prevColor !== nextColor) {
        return false; // 색상이 변경되었으므로 리렌더링 필요
      }
    }

    // getVariant 확인 (라벨의 변형) - LabelColumn에만 있음
    if (isLabelColumn(column) && column.getVariant) {
      const prevVariant = column.getVariant(prevProps.value, prevProps.row);
      const nextVariant = column.getVariant(nextProps.value, nextProps.row);
      if (prevVariant !== nextVariant) {
        return false; // 변형이 변경되었으므로 리렌더링 필요
      }
    }

    // link 확인 (링크의 to와 tabName)
    if (column.link) {
      const prevTo = column.link.to(prevProps.row || {});
      const nextTo = column.link.to(nextProps.row || {});
      if (prevTo !== nextTo) {
        return false; // link to가 변경되었으므로 리렌더링 필요
      }
    }
  }

  return true; // 실제 표시되는 내용이 변경되지 않았으므로 리렌더링 불필요
});
